import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Alert } from '@atoms';
import { CUSTOMER_SUPPORT, PERMISSIONS } from '@globalConstants';
import {
  useCancelDispersion,
  useCompanyDetails,
  useCustomerId,
  useShowAreaFeature,
  useRoles,
  useURLparams,
  useUser,
  usePermissions,
} from '@hooks';
import { Table } from '@molecules';
import { trackings } from '@utils';
import { COLUMNS, DEFAULT_PAGE_LIMIT } from './constants';
import { MY_DISPERSION_DETAIL_CLICK_EVENT } from './events';

/**
 * List of company dispersions
 * @param {object} props
 * @param {object} props.data - Paginated dispersions data
 * @param {boolean} props.isLoading - Loading state
 * @param {function} props.handleRowsPerPageChange - Rows per page handler
 * @param {function} props.handlePageChange - Page change handler
 * @param {function} props.handleRefresh - Refresh handler
 */
const DispersionsList = ({
  data,
  isLoading,
  handleRowsPerPageChange,
  handlePageChange,
  handleRefresh,
}) => {
  const { params } = useURLparams();
  const { customerId } = useCustomerId();
  const { data: companyData } = useCompanyDetails(customerId);
  const { user } = useUser();
  const navigate = useNavigate();
  const {
    showModal,
    closeModal,
    status,
    alertMessage,
    cancelDispersion,
    isLoading: isLoadingCancelDispersion,
  } = useCancelDispersion();
  const { t } = useTranslation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [dispersionCancelData, setDispersionCancelData] = useState({});
  const { showAreaFeature } = useShowAreaFeature();
  const { validateRoles } = useRoles();
  const isCustomerSupport = validateRoles([CUSTOMER_SUPPORT]);
  const isInternal = Boolean(user?.isInternal);
  const isDemo = usePermissions([PERMISSIONS.demoDeleteUsersCreate]);

  /** Navigate to detail page if processed status */
  const navigateToDetail = (row) => {
    trackings(MY_DISPERSION_DETAIL_CLICK_EVENT);
    row.status === 'processed' &&
      navigate(`/dispersiones/mis-dispersiones/${row.id}`, { state: row });
  };

  /** Handle click on row item */
  const handleClick = async (id, date) => {
    setDispersionCancelData({ id, date: date?.split(',')[0], time: date?.split(',')[1] });
    setShowConfirmModal(true);
  };

  const tableColumns = useMemo(() => {
    return COLUMNS(handleClick, showAreaFeature, isCustomerSupport, isInternal).filter(
      (item) => !item?.hide
    );
  }, [showAreaFeature, isCustomerSupport, isInternal]);

  /** Function to handle confirming the cancellation of a dispersion */
  const handleConfirmCancelDispersion = async () => {
    setShowConfirmModal(false);
    await cancelDispersion(customerId, dispersionCancelData?.id);
    handleRefresh();
  };

  /** Function to handle canceling the cancellation of a dispersion */
  const handleCloseCancelDispersion = () => {
    setDispersionCancelData({});
    setShowConfirmModal(false);
  };

  return (
    data?.page && (
      <>
        <Table
          count={data.totalCount || 0}
          columns={tableColumns}
          items={data.page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          page={params.page || 1}
          rowsPerPage={params.limit || DEFAULT_PAGE_LIMIT}
          onItemClick={navigateToDetail}
          isLoading={isLoading || isLoadingCancelDispersion}
          showTooltip={[t('DISPERSIONS:UPLOAD_DATE'), t('DISPERSIONS:DISPERSION_DATE')]}
        />
        <Alert
          variant='warning'
          modalVisible={showConfirmModal}
          handleClose={handleCloseCancelDispersion}
          handleSubmit={handleConfirmCancelDispersion}
          closeButtonText='COMMON:CANCEL'
          title={t('DISPERSIONS:CANCELLED:TITLE_MODAL_CONFIRM')}
          disabledConfirm={isDemo}
          submitLoading={isLoading}
          content={
            <Trans
              i18nKey='DISPERSIONS:CANCELLED:DESCRIPTION_MODAL_CONFIRM'
              values={{
                name: companyData?.description,
                date: dispersionCancelData?.date,
                time: dispersionCancelData?.time,
              }}
              components={{ bold: <strong /> }}
            />
          }
        />
        <Alert
          modalVisible={showModal}
          handleClose={closeModal}
          variant={status}
          title={alertMessage.title}
          description={alertMessage.description}
        />
      </>
    )
  );
};

DispersionsList.propTypes = {
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  handleRowsPerPageChange: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
};

export default DispersionsList;
